'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import useFetchLineFillPricing from '../../../hooks/useFetchLineFillPricing';
import { toast } from 'react-toastify';

const useUpdateLineFillPricing = () => {
  const updatePrice = async (id, patchDoc) => {
    try {
      const response = await axios.patch(
        `/api/PipelinePricing/linefill/update-pricing/${id}`,
        patchDoc,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
        }
      );
      return { success: true, message: response.data };
    } catch (error) {
      console.error('Update failed:', error);
      return { success: false, message: error?.response?.data || error.message };
    }
  };

  return { updatePrice };
};

const LineFillPricingGrid = () => {
  const { lineFillPricing, loading, error } = useFetchLineFillPricing();
  const { updatePrice } = useUpdateLineFillPricing();
  const [pricingData, setPricingData] = useState([]);
  const [editedRows, setEditedRows] = useState(new Map());
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!loading && !error) {
      setPricingData(lineFillPricing);
      setEditedRows(new Map());
    }
  }, [lineFillPricing, loading, error]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Item',
        field: 'item',
        flex: 2,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#333' },
      },
      {
        headerName: 'Unit',
        field: 'unit',
        editable: true,
        flex: 1,
      },
      {
        headerName: 'Price',
        field: 'price',
        editable: true,
        valueFormatter: ({ value }) =>
          value === '' || value === null || isNaN(Number(value))
            ? ''
            : Number(value).toLocaleString(),
        flex: 1,
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const { data, colDef, newValue } = event;
    if (!data.id) {
      console.warn('Missing ID for row:', data);
      return;
    }

    const updated = new Map(editedRows);
    if (!updated.has(data.id)) {
      updated.set(data.id, []);
    }

    updated.get(data.id).push({
      field: colDef.field,
      value: newValue
    });

    setEditedRows(updated);
  };

  const handleUpdatePricing = async () => {
    if (editedRows.size === 0) {
      toast.info('No changes to update.');
      return;
    }

    setUpdating(true);

    const patchPromises = Array.from(editedRows.entries()).map(([id, changes]) => {
      const patchDoc = changes.map((change) => ({
        op: 'replace',
        path: `/${change.field}`,
        value: change.value
      }));

      return updatePrice(id, patchDoc);
    });

    const results = await Promise.allSettled(patchPromises);

    let allSucceeded = true;
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        if (!result.value.success) {
          allSucceeded = false;
          const row = Array.from(editedRows.entries())[index];
          toast.error(`Failed to update row ID ${row[0]}: ${result.value.message}`);
        }
      } else {
        allSucceeded = false;
        const row = Array.from(editedRows.entries())[index];
        toast.error(`Failed to update row ID ${row[0]}: ${result.reason}`);
      }
    });

    if (allSucceeded) {
      toast.success('Line Fill pricing updated successfully!');
      setEditedRows(new Map());
    }

    setUpdating(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Line Fill Pricing Table
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Box className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
              <AgGridReact
                rowData={pricingData}
                columnDefs={columnDefs}
                onCellValueChanged={handleGridEdit}
                defaultColDef={{ resizable: true }}
                domLayout="autoHeight"
                getRowNodeId={(data) => data.id}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdatePricing}
              disabled={updating}
            >
              {updating ? 'Updating...' : 'Update Pricing'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LineFillPricingGrid;
